// realtimeApi.ts
// GraphQL client for AppSync with offline support

import { generateClient } from 'aws-amplify/api';

// Types
export interface ChunkResult {
  sessionId: string;
  delta_subjective: string[];
  delta_objective: string[];
  delta_plan: string[];
  timestamp: string;
}

export interface FinalResult {
  sessionId: string;
  final_note: string;
  status: 'processing' | 'completed' | 'failed';
  processing_duration_seconds: number;
  timestamp: string;
}

export interface ProcessStreamChunkInput {
  sessionId: string;
  text: string;
}

export interface FinishSessionInput {
  sessionId: string;
}

interface QueuedChunk {
  id: string;
  sessionId: string;
  text: string;
  timestamp: number;
  retryCount: number;
}

interface RealtimeApiConfig {
  apiUrl: string;
  region: string;
}

// GraphQL Operations
const PROCESS_STREAM_CHUNK = `
  mutation ProcessStreamChunk($input: ProcessStreamChunkInput!) {
    processStreamChunk(input: $input) {
      sessionId
      delta_subjective
      delta_objective
      delta_plan
      timestamp
    }
  }
`;

const FINISH_SESSION = `
  mutation FinishSession($input: FinishSessionInput!) {
    finishSession(input: $input) {
      sessionId
      final_note
      status
      processing_duration_seconds
      timestamp
    }
  }
`;

const ON_NOTE_UPDATED = `
  subscription OnNoteUpdated($sessionId: ID!) {
    onNoteUpdated(sessionId: $sessionId) {
      sessionId
      delta_subjective
      delta_objective
      delta_plan
      timestamp
    }
  }
`;

const OFFLINE_QUEUE_KEY = 'psybytes_offline_queue';
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000];

class RealtimeApiService {
  private config: RealtimeApiConfig | null = null;
  private client: ReturnType<typeof generateClient> | null = null;
  private offlineQueue: QueuedChunk[] = [];
  private isOnline: boolean = true;
  private processingQueue: boolean = false;
  private initialized: boolean = false;

  constructor() {
    // Load offline queue from localStorage
    this.loadOfflineQueue();

    // Set up online/offline listeners
    if (typeof window !== 'undefined') {
      this.isOnline = navigator.onLine;
      window.addEventListener('online', this.handleOnline.bind(this));
      window.addEventListener('offline', this.handleOffline.bind(this));
    }
  }

  initialize(config: RealtimeApiConfig): void {
    if (this.initialized) {
      console.log('[RealtimeApi] Already initialized');
      return;
    }
    this.config = config;
    console.log('[RealtimeApi] Initialized with config:', config);
    this.initialized = true;
  }

  private ensureClient(): void {
    if (!this.client && this.initialized) {
      try {
        this.client = generateClient();
        console.log('[RealtimeApi] GraphQL client created');
      } catch (err) {
        console.error('[RealtimeApi] Failed to generate client:', err);
      }
    }
  }

  private loadOfflineQueue(): void {
    try {
      const stored = localStorage.getItem(OFFLINE_QUEUE_KEY);
      if (stored) {
        this.offlineQueue = JSON.parse(stored);
        console.log(`[RealtimeApi] Loaded ${this.offlineQueue.length} queued chunks from storage`);
      }
    } catch (err) {
      console.error('[RealtimeApi] Failed to load offline queue:', err);
      this.offlineQueue = [];
    }
  }

  private saveOfflineQueue(): void {
    try {
      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(this.offlineQueue));
    } catch (err) {
      console.error('[RealtimeApi] Failed to save offline queue:', err);
    }
  }

  private handleOnline(): void {
    console.log('[RealtimeApi] Network online');
    this.isOnline = true;
    this.processOfflineQueue();
  }

  private handleOffline(): void {
    console.log('[RealtimeApi] Network offline');
    this.isOnline = false;
  }

  private async processOfflineQueue(): Promise<void> {
    if (this.processingQueue || this.offlineQueue.length === 0) return;

    this.processingQueue = true;
    console.log(`[RealtimeApi] Processing ${this.offlineQueue.length} queued chunks`);

    const chunksToProcess = [...this.offlineQueue];
    this.offlineQueue = [];
    this.saveOfflineQueue();

    for (const chunk of chunksToProcess) {
      try {
        await this.processStreamChunk({
          sessionId: chunk.sessionId,
          text: chunk.text,
        });
        console.log(`[RealtimeApi] Successfully processed queued chunk ${chunk.id}`);
      } catch (err) {
        console.error(`[RealtimeApi] Failed to process queued chunk ${chunk.id}:`, err);

        // Re-queue if under retry limit
        if (chunk.retryCount < MAX_RETRIES) {
          this.offlineQueue.push({
            ...chunk,
            retryCount: chunk.retryCount + 1,
          });
        } else {
          console.warn(`[RealtimeApi] Chunk ${chunk.id} exceeded max retries, discarding`);
        }
      }

      // Add delay between retries
      if (chunk.retryCount > 0) {
        const delay = RETRY_DELAYS[Math.min(chunk.retryCount, RETRY_DELAYS.length - 1)];
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    this.saveOfflineQueue();
    this.processingQueue = false;
  }

  private queueChunk(input: ProcessStreamChunkInput): void {
    const chunk: QueuedChunk = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      sessionId: input.sessionId,
      text: input.text,
      timestamp: Date.now(),
      retryCount: 0,
    };

    this.offlineQueue.push(chunk);
    this.saveOfflineQueue();
    console.log(`[RealtimeApi] Queued chunk ${chunk.id} for later processing`);
  }

  async processStreamChunk(input: ProcessStreamChunkInput): Promise<ChunkResult | null> {
    // Check if offline - queue the chunk
    if (!this.isOnline || !navigator.onLine) {
      this.queueChunk(input);
      return null;
    }

    // Ensure client is created (lazy initialization after Amplify is configured)
    this.ensureClient();

    if (!this.client) {
      console.error('[RealtimeApi] Client not initialized');
      this.queueChunk(input);
      return null;
    }

    try {
      const result = await this.client.graphql({
        query: PROCESS_STREAM_CHUNK,
        variables: { input },
      }) as { data: { processStreamChunk: ChunkResult } };

      console.log('[RealtimeApi] processStreamChunk result:', result.data.processStreamChunk);
      return result.data.processStreamChunk;
    } catch (err: any) {
      console.error('[RealtimeApi] processStreamChunk error:', err);

      // Check if it's a network error
      if (err.message?.includes('Network') || !navigator.onLine) {
        this.isOnline = false;
        this.queueChunk(input);
        return null;
      }

      throw err;
    }
  }

  async finishSession(input: FinishSessionInput): Promise<FinalResult> {
    // Ensure client is created (lazy initialization after Amplify is configured)
    this.ensureClient();

    if (!this.client) {
      throw new Error('RealtimeApi client not initialized');
    }

    // Process any remaining queued chunks for this session first
    await this.processOfflineQueue();

    try {
      const result = await this.client.graphql({
        query: FINISH_SESSION,
        variables: { input },
      }) as { data: { finishSession: FinalResult } };

      console.log('[RealtimeApi] finishSession result:', result.data.finishSession);
      return result.data.finishSession;
    } catch (err) {
      console.error('[RealtimeApi] finishSession error:', err);
      throw err;
    }
  }

  subscribeToUpdates(
    sessionId: string,
    onUpdate: (data: ChunkResult) => void,
    onError: (error: Error) => void
  ): { unsubscribe: () => void } {
    // Ensure client is created (lazy initialization after Amplify is configured)
    this.ensureClient();

    if (!this.client) {
      console.error('[RealtimeApi] Client not initialized');
      return { unsubscribe: () => {} };
    }

    try {
      const subscription = this.client.graphql({
        query: ON_NOTE_UPDATED,
        variables: { sessionId },
      });

      // Handle subscription based on the Amplify v6 API
      if (subscription && typeof (subscription as any).subscribe === 'function') {
        const sub = (subscription as any).subscribe({
          next: ({ data }: { data: { onNoteUpdated: ChunkResult } }) => {
            if (data?.onNoteUpdated) {
              onUpdate(data.onNoteUpdated);
            }
          },
          error: (err: Error) => {
            console.error('[RealtimeApi] Subscription error:', err);
            onError(err);
          },
        });

        return {
          unsubscribe: () => {
            sub.unsubscribe();
          },
        };
      }

      return { unsubscribe: () => {} };
    } catch (err) {
      console.error('[RealtimeApi] Failed to subscribe:', err);
      return { unsubscribe: () => {} };
    }
  }

  clearQueueForSession(sessionId: string): void {
    this.offlineQueue = this.offlineQueue.filter(chunk => chunk.sessionId !== sessionId);
    this.saveOfflineQueue();
    console.log(`[RealtimeApi] Cleared queue for session ${sessionId}`);
  }

  getQueueStatus(): { count: number; isOnline: boolean } {
    return {
      count: this.offlineQueue.length,
      isOnline: this.isOnline,
    };
  }
}

// Singleton export
export const realtimeApi = new RealtimeApiService();
